import { type DefaultContext, mutationPromise } from '@egw/clients'
import {
  type EnrollmentMultiBankTransferData,
  type EnrollmentMultiBankTransferSubmitInput,
  type EnrollmentMultiBankTransferSubmitOutput,
} from '../../../../models'
import { createMutationHook, createQueryHook } from '../../../../hooks'

/**
 * Get Enrollment Multi BankTransfer data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentMultiBankTransferData>
 * @example const response = await getEnrollmentMultiBankTransferData()
 */
export const getEnrollmentMultiBankTransferData = async (
  context?: DefaultContext,
): Promise<EnrollmentMultiBankTransferData> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentMultiBankTransferData',
      query: `mutation getEnrollmentMultiBankTransferData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
            enrollmentData {
              applicationId
              fundingAccountName
            }
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: {},
    },
    context,
  )) as { submitEnrollment: EnrollmentMultiBankTransferData }
  return response.submitEnrollment
}

export const useGetEnrollmentMultiBankTransferData = createQueryHook<undefined, EnrollmentMultiBankTransferData>(
  getEnrollmentMultiBankTransferData,
)

/**
 * Get Plaid Linking Token
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentMultiBankTransferSubmitOutput>
 * @example const response = await getEnrollmentMultiPlaidToken();
 */
export const getEnrollmentMultiPlaidToken = async (
  enrollmentMultiBankTransferSubmitInput: EnrollmentMultiBankTransferSubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentMultiBankTransferSubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentMultiPlaidToken',
      query: `mutation getEnrollmentMultiPlaidToken($submitEnrollmentInput: SubmitEnrollmentInput!) {
      submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
        ... on SubmitEnrollmentSuccess {
          __typename
          enrollmentData {
            plaidLinkToken
          }
        }
        ... on SubmitEnrollmentError {
          __typename
          path
        }
      }
    }`,
    },
    {
      submitEnrollmentInput: enrollmentMultiBankTransferSubmitInput,
    },
    context,
  )) as { submitEnrollment: EnrollmentMultiBankTransferSubmitOutput }
  return response.submitEnrollment
}

export const useGetEnrollmentMultiPlaidToken = createMutationHook<
  EnrollmentMultiBankTransferSubmitInput,
  EnrollmentMultiBankTransferSubmitOutput
>(getEnrollmentMultiPlaidToken)

/**
 * Submit Enrollment Multi BankTransfer data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentMultiBankTransferSubmitOutput>
 * @example const response = await submitEnrollmentMultiBankTransferData();
 */
export const submitEnrollmentMultiBankTransferData = async (
  enrollmentBankTransferSubmitInput: EnrollmentMultiBankTransferSubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentMultiBankTransferSubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentMultiBankTransferData',
      query: `mutation submitEnrollmentMultiBankTransferData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
            enrollmentData {
              transferFundsInitiated
            }
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: enrollmentBankTransferSubmitInput,
    },
    context,
  )) as { submitEnrollment: EnrollmentMultiBankTransferSubmitOutput }
  return response.submitEnrollment
}

export const useSubmitEnrollmentMultiBankTransferData = createMutationHook<
  EnrollmentMultiBankTransferSubmitInput,
  EnrollmentMultiBankTransferSubmitOutput
>(submitEnrollmentMultiBankTransferData)
